import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTheme, styled } from '@mui/material/styles';
import { Grid, Stack, Typography, Link, useMediaQuery, CircularProgress } from '@mui/material';

// project imports
import AuthCardWrapper from '../../components/Cards/AuthCardWrapper';
import logoImg from '../../assets/images/raptor_tech_logo.png';
import { ssoLogin } from 'components/../redux/actions/auth';

const AuthWrapper = styled('div')(({ theme }) => ({
    background: `linear-gradient(white, ${theme.palette.primary.main})`
}));

// Landing page for the SAML ACS redirect (/login/sso_callback).
// Success: ?token=<knox>&email=...&return_to=...  -> complete the session.
// Failure: ?error=<message>                        -> show why and offer password login.
const SsoCallback = () => {
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const started = useRef(false);

    const token = searchParams.get('token');
    const error = searchParams.get('error');
    const returnTo = searchParams.get('return_to') || '/';

    useEffect(() => {
        if (started.current || error) return;
        if (token) {
            started.current = true;
            dispatch(ssoLogin({ token }, navigate, returnTo));
        }
    }, [dispatch, navigate, token, returnTo, error]);

    return (
        <AuthWrapper>
            <Grid container direction="column" justifyContent="flex-end" sx={{ minHeight: '100vh' }}>
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 68px)' }}>
                        <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
                            <AuthCardWrapper>
                                <Grid container spacing={2} alignItems="center" justifyContent="center">
                                    <Grid item sx={{ mb: 3 }}>
                                        <Link href="/">
                                            <img src={logoImg} width="150" alt="" />
                                        </Link>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Stack alignItems="center" justifyContent="center" spacing={2}>
                                            {error ? (
                                                <>
                                                    <Typography
                                                        color={theme.palette.error.main}
                                                        variant={matchDownSM ? 'h4' : 'h3'}
                                                        align="center"
                                                    >
                                                        Single sign-on could not complete
                                                    </Typography>
                                                    <Typography variant="body1" align="center">
                                                        {error}
                                                    </Typography>
                                                    <Link href="/login" sx={{ textDecoration: 'none' }}>
                                                        Back to login
                                                    </Link>
                                                </>
                                            ) : (
                                                <>
                                                    <CircularProgress />
                                                    <Typography variant="subtitle1" align="center">
                                                        {token ? 'Signing you in…' : 'No sign-on information was provided.'}
                                                    </Typography>
                                                    {!token && (
                                                        <Link href="/login" sx={{ textDecoration: 'none' }}>
                                                            Back to login
                                                        </Link>
                                                    )}
                                                </>
                                            )}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </AuthCardWrapper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </AuthWrapper>
    );
};

export default SsoCallback;
